import React from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Switch,
  FormControlLabel,
  Button,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Settings as SettingsIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';

const CookiesPage: React.FC = () => {
  const { t } = useTranslation();

  const cookieTypes = [
    {
      icon: <CheckCircleIcon />,
      title: t('cookies.necessary'),
      description: t('cookies.necessaryDesc'),
      color: '#10b981',
      required: true,
      examples: t('cookies.necessaryExamples', { returnObjects: true }) as string[],
    },
    {
      icon: <SettingsIcon />,
      title: t('cookies.functional'),
      description: t('cookies.functionalDesc'),
      color: '#28A0F0',
      required: false,
      examples: t('cookies.functionalExamples', { returnObjects: true }) as string[],
    },
    {
      icon: <InfoIcon />,
      title: t('cookies.analytical'),
      description: t('cookies.analyticalDesc'),
      color: '#f59e0b',
      required: false,
      examples: t('cookies.analyticalExamples', { returnObjects: true }) as string[],
    },
    {
      icon: <SecurityIcon />,
      title: t('cookies.marketing'),
      description: t('cookies.marketingDesc'),
      color: '#ec4899',
      required: false,
      examples: [
        'Cookies за персонализирана реклама',
        'Cookies за проследяване на рекламни кампании',
        'Cookies за ретаргетинг',
        'Cookies за социални мрежи',
      ],
    },
  ];

  const cookieSettings = [
    { name: 'necessary', label: 'Необходими cookies', enabled: true, disabled: true },
    { name: 'functional', label: 'Функционални cookies', enabled: false, disabled: false },
    { name: 'analytics', label: 'Аналитични cookies', enabled: false, disabled: false },
    { name: 'marketing', label: 'Маркетингови cookies', enabled: false, disabled: false },
  ];

  return (
    <Box sx={{ backgroundColor: '#0f0f23', minHeight: '100vh' }}>
      <Header />
      
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
        {/* Hero Section */}
        <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
          <Typography
            variant="h2"
            sx={{
              color: '#ffffff',
              fontWeight: 800,
              mb: 3,
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              lineHeight: { xs: 1.2, md: 1.3 },
              background: 'linear-gradient(135deg, #ffffff 0%, #28A0F0 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
{t('cookies.title')}
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: '#a0a0a0',
              mb: 4,
              maxWidth: 800,
              mx: 'auto',
              fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
              lineHeight: { xs: 1.4, md: 1.5 },
              px: { xs: 2, sm: 0 },
            }}
          >
{t('cookies.subtitle')}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#a0a0a0',
              fontSize: { xs: '0.8rem', md: '0.875rem' },
            }}
          >
{t('cookies.lastUpdated')}: {new Date().toLocaleDateString('bg-BG')}
          </Typography>
        </Box>

        {/* Introduction */}
        <Card
          sx={{
            background: 'linear-gradient(135deg, rgba(40, 160, 240, 0.1) 0%, rgba(236, 72, 153, 0.1) 100%)',
            border: '1px solid rgba(40, 160, 240, 0.3)',
            borderRadius: 4,
            mb: 6,
            overflow: 'hidden',
          }}
        >
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Typography
              variant="h5"
              sx={{
                color: '#ffffff',
                fontWeight: 700,
                mb: 3,
                fontSize: { xs: '1.25rem', md: '1.5rem' },
              }}
            >
{t('cookies.whatAreCookies')}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#d0d0d0',
                mb: 3,
                fontSize: { xs: '0.9rem', md: '1rem' },
                lineHeight: 1.6,
              }}
            >
              Cookies са малки текстови файлове, които се съхраняват на вашето устройство 
              когато посещавате уебсайт. Те помагат на сайта да запомни информация за вас 
              и вашите предпочитания, което прави използването му по-удобно и персонализирано.
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#d0d0d0',
                fontSize: { xs: '0.9rem', md: '1rem' },
                lineHeight: 1.6,
              }}
            >
              Workio използва cookies за да подобри вашето потребителско изживяване, 
              да анализира използването на платформата и да предостави персонализирани услуги.
            </Typography>
          </CardContent>
        </Card>

        {/* Cookie Types */}
        <Box sx={{ mb: { xs: 6, md: 8 } }}>
          <Typography
            variant="h4"
            sx={{
              color: '#ffffff',
              fontWeight: 700,
              mb: 4,
              textAlign: 'center',
              fontSize: { xs: '1.5rem', md: '2rem' },
            }}
          >
{t('cookies.cookieTypes')}
          </Typography>
          {cookieTypes.map((type, index) => (
            <Card
              key={index}
              sx={{
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                borderRadius: 4,
                mb: 3,
                overflow: 'hidden',
              }}
            >
              <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Box
                    sx={{
                      color: type.color,
                      mr: 2,
                      '& .MuiSvgIcon-root': {
                        fontSize: { xs: 32, md: 40 },
                      },
                    }}
                  >
                    {type.icon}
                  </Box>
                  <Box>
                    <Typography
                      variant="h5"
                      sx={{
                        color: '#ffffff',
                        fontWeight: 700,
                        fontSize: { xs: '1.25rem', md: '1.5rem' },
                      }}
                    >
                      {type.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#a0a0a0',
                        fontSize: { xs: '0.8rem', md: '0.875rem' },
                      }}
                    >
                      {type.description}
                    </Typography>
                  </Box>
                </Box>
                <List>
                  {type.examples.map((example, exampleIndex) => (
                    <ListItem key={exampleIndex} sx={{ px: 0, py: 0.5 }}>
                      <ListItemText
                        primary={
                          <Typography
                            variant="body1"
                            sx={{
                              color: '#d0d0d0',
                              fontSize: { xs: '0.9rem', md: '1rem' },
                              lineHeight: 1.6,
                            }}
                          >
                            • {example}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          ))}
        </Box>

        {/* Cookie Settings */}
        <Card
          sx={{
            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(40, 160, 240, 0.1) 100%)',
            border: '1px solid rgba(16, 185, 129, 0.3)',
            borderRadius: 4,
            mb: 6,
            overflow: 'hidden',
          }}
        >
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Typography
              variant="h5"
              sx={{
                color: '#ffffff',
                fontWeight: 700,
                mb: 3,
                fontSize: { xs: '1.25rem', md: '1.5rem' },
              }}
            >
{t('cookies.cookieManagement')}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#d0d0d0',
                mb: 4,
                fontSize: { xs: '0.9rem', md: '1rem' },
                lineHeight: 1.6,
              }}
            >
              Можете да контролирате кои cookies да приемете. Имайте предвид, че 
              деактивирането на някои cookies може да повлияе на функционалността на сайта.
            </Typography>
            <Box sx={{ mb: 4 }}>
              {cookieSettings.map((setting, index) => (
                <FormControlLabel
                  key={index}
                  control={
                    <Switch
                      checked={setting.enabled}
                      disabled={setting.disabled}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: setting.disabled ? '#10b981' : '#28A0F0',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: setting.disabled ? '#10b981' : '#28A0F0',
                        },
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography
                        variant="body1"
                        sx={{
                          color: '#ffffff',
                          fontWeight: 600,
                          fontSize: { xs: '0.9rem', md: '1rem' },
                        }}
                      >
                        {setting.label}
                      </Typography>
                      {setting.disabled && (
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#a0a0a0',
                            fontSize: { xs: '0.8rem', md: '0.875rem' },
                          }}
                        >
                          (Задължителни за функционирането на сайта)
                        </Typography>
                      )}
                    </Box>
                  }
                  sx={{ display: 'block', mb: 2 }}
                />
              ))}
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                sx={{
                  background: 'linear-gradient(135deg, #28A0F0 0%, #1d4ed8 100%)',
                  color: '#ffffff',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
                  },
                }}
              >
                Запази настройките
              </Button>
              <Button
                variant="outlined"
                sx={{
                  borderColor: '#28A0F0',
                  color: '#28A0F0',
                  '&:hover': {
                    borderColor: '#1d4ed8',
                    backgroundColor: 'rgba(40, 160, 240, 0.1)',
                  },
                }}
              >
                Приеми всички
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Third Party Cookies */}
        <Card
          sx={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            borderRadius: 4,
            mb: 6,
            overflow: 'hidden',
          }}
        >
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Typography
              variant="h5"
              sx={{
                color: '#ffffff',
                fontWeight: 700,
                mb: 3,
                fontSize: { xs: '1.25rem', md: '1.5rem' },
              }}
            >
{t('cookies.thirdPartyCookies')}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#d0d0d0',
                mb: 3,
                fontSize: { xs: '0.9rem', md: '1rem' },
                lineHeight: 1.6,
              }}
            >
              Workio може да използва услуги на трети страни, които поставят свои собствени 
              cookies на вашето устройство. Тези cookies се управляват от съответните трети страни.
            </Typography>
            <List>
              {[
                'Google Analytics - за анализ на трафика и поведението на потребителите',
                'Google Ads - за показване на релевантна реклама',
                'Facebook Pixel - за проследяване на конверсии и ретаргетинг',
                'Stripe - за обработка на плащания',
                'Intercom - за чат поддръжка и комуникация',
              ].map((service, index) => (
                <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                  <ListItemText
                    primary={
                      <Typography
                        variant="body1"
                        sx={{
                          color: '#d0d0d0',
                          fontSize: { xs: '0.9rem', md: '1rem' },
                          lineHeight: 1.6,
                        }}
                      >
                        • {service}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>

        {/* Contact Information */}
        <Card
          sx={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            borderRadius: 4,
            overflow: 'hidden',
          }}
        >
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Typography
              variant="h5"
              sx={{
                color: '#ffffff',
                fontWeight: 700,
                mb: 3,
                fontSize: { xs: '1.25rem', md: '1.5rem' },
              }}
            >
{t('cookies.contactCookies')}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#d0d0d0',
                mb: 2,
                fontSize: { xs: '0.9rem', md: '1rem' },
                lineHeight: 1.6,
              }}
            >
              Ако имате въпроси относно използването на cookies в Workio, моля свържете се с нас:
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  color: '#28A0F0',
                  fontSize: { xs: '0.8rem', md: '0.875rem' },
                }}
              >
                Имейл: privacy@workio.bg
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#28A0F0',
                  fontSize: { xs: '0.8rem', md: '0.875rem' },
                }}
              >
                Телефон: +359 888 123 456
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#28A0F0',
                  fontSize: { xs: '0.8rem', md: '0.875rem' },
                }}
              >
                Адрес: ул. Витоша 12, София 1000, България
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>

      <Footer />
    </Box>
  );
};

export default CookiesPage;
